export const inviteesEmptyStateText = "No one has been invited yet.";

export const formatInviteeName = (name: string | null): string => name ?? "(no name)";

export interface InviteePickerNotice {
    type: "success" | "info" | "error";
    text: string;
}

export const bulkAddFailedNotice = (): InviteePickerNotice => ({
    type: "error",
    text: "Those guests could not be added. Please try again.",
});

const noticeName = (name: string | null): string => name ?? "This contact";

export const inviteeAddedNotice = (name: string | null): InviteePickerNotice => ({
    type: "success",
    text: `${noticeName(name)} was added to the guest list.`,
});

export const inviteeAlreadyListedNotice = (name: string | null): InviteePickerNotice => ({
    type: "info",
    text: `${noticeName(name)} is already on the guest list.`,
});

export const bulkAddedNotice = (
    added: number,
    requested: number,
): InviteePickerNotice => {
    if (requested === 0) {
        return { type: "info", text: "No contacts matched your search." };
    }
    if (added === 0) {
        return { type: "info", text: "Everyone matching is already on the guest list." };
    }
    const noun = added === 1 ? "guest" : "guests";
    const skipped = requested - added;
    if (skipped > 0) {
        const skippedNoun = skipped === 1 ? "was" : "were";
        return {
            type: "success",
            text: `Added ${added} ${noun}. ${skipped} ${skippedNoun} already on the guest list.`,
        };
    }
    return { type: "success", text: `Added ${added} ${noun} to the guest list.` };
};

export interface InviteeSummaryCounts {
    total: number;
    yes: number;
    no: number;
    maybe: number;
    notYetInvited: number;
}

export const rosterSummaryCounts = (
    invitees: { rsvpResponse?: string | null; invitedAt?: string | null }[],
): InviteeSummaryCounts => ({
    total: invitees.length,
    yes: invitees.filter(invitee => invitee.rsvpResponse === "yes").length,
    no: invitees.filter(invitee => invitee.rsvpResponse === "no").length,
    maybe: invitees.filter(invitee => invitee.rsvpResponse === "maybe").length,
    notYetInvited: invitees.filter(invitee => !invitee.invitedAt).length,
});

export const inviteeSummaryText = (counts: InviteeSummaryCounts): string => {
    if (counts.total === 0) {
        return "No invitees yet";
    }
    const noun = counts.total === 1 ? "invitee" : "invitees";
    const parts = [`${counts.total} ${noun}`];
    if (counts.yes > 0) {
        parts.push(`${counts.yes} yes`);
    }
    if (counts.maybe > 0) {
        parts.push(`${counts.maybe} maybe`);
    }
    if (counts.no > 0) {
        parts.push(`${counts.no} no`);
    }
    if (counts.notYetInvited > 0) {
        parts.push(`${counts.notYetInvited} not yet invited`);
    }
    return parts.join(" · ");
};

export const inviteFlowExplainerText =
    "Email invitations to everyone with an address on file, or copy a guest's personal RSVP link to send it yourself. Either way, mark them invited so you can see who has heard from you.";

export const invitationResultText = (result: {
    queued: number;
    skipped: number;
}): string => {
    const noun = result.queued === 1 ? "invitation is" : "invitations are";
    const sent = `${result.queued} ${noun} on the way.`;
    if (result.skipped === 0) {
        return sent;
    }
    const skippedNoun = result.skipped === 1 ? "guest has" : "guests have";
    return `${sent} ${result.skipped} ${skippedNoun} no email address — copy their RSVP link instead.`;
};
